"use client";

import { SearchResultItem } from "./SearchResultItem";
import { Typography } from "@/components/Typography";
import { PopoverContent } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/EmptyState";
import {
  getSearchTypeLabel,
  GlobalRealtimeSearchRes,
  GlobalSearchSuggesstionRes,
  GlobalTrendingSearchRes,
  RecommendServiceItem,
} from "./global-search.type";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type SearchSuggestionsPopoverProps = {
  items?: GlobalSearchSuggesstionRes;
  realtimeItems?: GlobalRealtimeSearchRes[];
  trendingItems?: GlobalTrendingSearchRes;
  isDestination: boolean;
  isSearching: boolean;
  onNavigate: (item: {
    id?: string | number;
    type?: string;
    label?: string;
  }) => void;
  onClear: () => void;
  isLoading?: boolean;
  className?: string;
};

const ResultSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex items-center gap-2 p-1 animate-pulse">
        <div className="h-10 w-10 shrink-0 rounded-md bg-subtle" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-2/3 rounded bg-subtle" />
          <div className="h-2.5 w-1/3 rounded bg-subtle" />
        </div>
      </div>
    ))}
  </div>
);

const SearchSuggestionsPopover = ({
  items,
  realtimeItems,
  trendingItems,
  isDestination,
  isSearching,
  onNavigate,
  onClear,
  isLoading,
  className,
}: SearchSuggestionsPopoverProps) => {
  const isMdDown = useMediaQuery("(min-width: 768px)");
  const hasHistories = (items?.histories?.length ?? 0) > 0;
  const hasRecent = (items?.recentSearches?.length ?? 0) > 0;
  const hasRecommendLocations = (items?.recommendLocations?.length ?? 0) > 0;
  const hasRecommendServices = (items?.recommendServices?.length ?? 0) > 0;
  const hasRealtimeData = realtimeItems && realtimeItems.length > 0;
  const hasTrendingData = (trendingItems?.trending?.length ?? 0) > 0;
  const hasAnySuggestions =
    hasHistories || hasRecent || hasRecommendLocations || hasRecommendServices;

  return (
    <PopoverContent
      onOpenAutoFocus={(e) => e.preventDefault()}
      sideOffset={8}
      align={isMdDown ? "start" : "center"}
      className={cn(
        "z-50 w-[calc(100vw-16px)] md:w-[500px] max-h-[min(600px,calc(100dvh-120px))] overflow-y-auto overscroll-contain rounded-3xl border bg-white p-4 shadow-float-panel outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
    >
      {/* Suggestion: Search Result */}
      {!isSearching && (
        <>
          {isLoading ? (
            <ResultSkeleton />
          ) : isDestination ? (
            hasTrendingData ? (
              <section className="space-y-4">
                <Typography variant="subtitle1">Trending</Typography>
                <div className="space-y-2">
                  {trendingItems?.trending?.map((item, index) => (
                    <SearchResultItem
                      key={index}
                      name={item.name}
                      located={item.located}
                      thumbnail={item.thumbnail}
                      badge={item.badge}
                      badgeLabel={getSearchTypeLabel(item.type) ?? "N/A"}
                      onClick={() => onNavigate(item)}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <EmptyState
                variant="not-available"
                title="Nothing here yet"
                description="Start typing to search hotels, rentals, activities, and more."
              />
            )
          ) : hasAnySuggestions ? (
            <main className="space-y-4">
              {/* History Keywords */}
              {(hasHistories || hasRecent) && (
                <section className="space-y-4">
                  <div className="flex justify-between">
                    <Typography variant="subtitle1">History</Typography>
                    <button
                      type="button"
                      onClick={onClear}
                      className="text-secondary"
                    >
                      Clear
                    </button>
                  </div>

                  <div className="flex flex-nowrap gap-2 overflow-x-auto no-scrollbar">
                    {items?.histories?.map((item, index) => (
                      <div
                        key={item.id ?? `${item.keyword}-${index}`}
                        className="shrink-0"
                      >
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onNavigate({ label: item.keyword })}
                          className="card-hover py-2"
                        >
                          <Typography
                            variant="body1"
                            className="rounded-full px-4 items-center py-1 bg-subtle w-fit whitespace-nowrap"
                          >
                            {item.keyword}
                          </Typography>
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {hasRecent && <Separator />}

              {/* Recent Searches */}
              {hasRecent && (
                <section className="space-y-4">
                  <Typography variant="subtitle1">
                    Recent Search Results
                  </Typography>
                  <div className="space-y-2">
                    {items?.recentSearches?.map((item, index) => (
                      <SearchResultItem
                        key={index}
                        name={item.name}
                        located={item.located}
                        thumbnail={item.thumbnail}
                        badge={item.badge}
                        badgeLabel={getSearchTypeLabel(item.type) ?? "N/A"}
                        onClick={() => onNavigate(item)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {(hasHistories || hasRecent) && hasRecommendLocations && (
                <Separator />
              )}

              {/* Recommended Destinations */}
              {hasRecommendLocations && (
                <section className="space-y-4">
                  <Typography variant="subtitle1">
                    Recommended Destinations
                  </Typography>
                  <div className="grid grid-cols-3 gap-y-2">
                    {items?.recommendLocations?.map((item, index) => (
                      <div
                        key={item.id}
                        className={cn(index % 3 !== 2 && "border-r")}
                      >
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() =>
                            onNavigate({
                              id: item.id,
                              type: "location",
                            } as RecommendServiceItem)
                          }
                          className="flex w-full min-w-0 items-center gap-2 px-3 py-2 text-start card-hover hover:bg-subtle hover:rounded-full"
                        >
                          <Typography variant="subtitle2" className="truncate">
                            {item.name}
                          </Typography>
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {(hasHistories || hasRecent || hasRecommendLocations) &&
                hasRecommendServices && <Separator />}

              {/* Recommended Services */}
              {hasRecommendServices && (
                <section className="space-y-4">
                  <Typography variant="subtitle1">
                    Let&apos;s Plan Your Perfect Getaway
                  </Typography>
                  <div className="space-y-2">
                    {items?.recommendServices?.map((item, index) => (
                      <SearchResultItem
                        key={`${item.id}-${index}`}
                        name={item.name}
                        located={item.located}
                        thumbnail={item.thumbnail}
                        badge={item.badge}
                        badgeLabel={getSearchTypeLabel(item.type) ?? "N/A"}
                        onClick={() => onNavigate(item)}
                      />
                    ))}
                  </div>
                </section>
              )}
            </main>
          ) : (
            <EmptyState
              variant="not-available"
              title="Nothing here yet"
              description="Start typing to search hotels, rentals, activities, and more."
            />
          )}
        </>
      )}

      {/* Realtime: Search Result */}
      {isSearching && (
        <>
          {isLoading ? (
            <ResultSkeleton />
          ) : hasRealtimeData ? (
            <>
              <Typography variant="subtitle1">Search Results</Typography>

              <div className="space-y-2 mt-4">
                {realtimeItems?.map((item, index) => (
                  <SearchResultItem
                    key={index}
                    name={item.name}
                    located={item.located}
                    thumbnail={item.thumbnail}
                    badge={item.badge}
                    badgeLabel={getSearchTypeLabel(item.type) ?? "N/A"}
                    onClick={() => onNavigate(item)}
                  />
                ))}
              </div>
            </>
          ) : (
            <EmptyState
              variant="not-available"
              title="No results found"
              description="Looks like we took a wrong turn! Try another search or explore top destinations."
            />
          )}
        </>
      )}
    </PopoverContent>
  );
};

export default SearchSuggestionsPopover;
